import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { 
  Building2, 
  MapPin, 
  Download, 
  Play, 
  ChevronLeft, 
  ChevronRight, 
  X,
  Bed,
  Bath,
  Maximize,
  TrendingUp,
  Calendar,
  Shield,
  Award,
  ArrowRight,
  Pause,
  Waves,
  Sparkles,
  Users,
  Dumbbell,
  Film,
  Calculator,
  DollarSign,
  Percent,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectShowcase = () => {
  const { language } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [activeUnit, setActiveUnit] = useState(0);
  const [showROICalculator, setShowROICalculator] = useState(false);
  const [showMasterplan, setShowMasterplan] = useState(false);
  
  // ROI Calculator state
  const [roiInputs, setRoiInputs] = useState({
    occupancyRate: 70,
    nightlyRate: 150,
    annualExpenses: 5000,
    investmentYears: 5
  });

  // Real data from Bali Life x Terra Sky Kaba-Kaba PDF
  const project = {
    id: 'bali-life-terra-sky-kaba-kaba',
    name: {
      fr: 'Bali Life x Terra Sky',
      en: 'Bali Life x Terra Sky',
      id: 'Bali Life x Terra Sky',
      es: 'Bali Life x Terra Sky'
    },
    subtitle: {
      fr: 'Kaba-Kaba',
      en: 'Kaba-Kaba',
      id: 'Kaba-Kaba',
      es: 'Kaba-Kaba'
    },
    tagline: {
      fr: 'L\'Art de Vivre Tropical',
      en: 'The Art of Tropical Living',
      id: 'Seni Hidup Tropis',
      es: 'El Arte del Vivir Tropical'
    },
    description: {
      fr: 'Au cœur d\'un Bali authentique et préservé, Terra Sky Kaba-Kaba propose une nouvelle vision de l\'art de vivre tropical. Architecture contemporaine, nature et intimité se rencontrent dans cette résidence privée de 2 500 m². Des lofts 1 chambre aux villas 3 chambres, chaque unité dispose d\'une piscine privée et est entièrement meublée.',
      en: 'In the heart of an authentic and preserved Bali, Terra Sky Kaba-Kaba offers a new vision of tropical living. Contemporary architecture, nature and privacy meet in this 2,500 m² private residence. From 1-bedroom lofts to 3-bedroom villas, each unit features a private pool and is fully furnished.',
      id: 'Di jantung Bali yang otentik dan terjaga, Terra Sky Kaba-Kaba menawarkan visi baru kehidupan tropis. Arsitektur kontemporer, alam, dan privasi bertemu dalam residensi pribadi seluas 2.500 m² ini. Dari loft 1 kamar hingga villa 3 kamar, setiap unit memiliki kolam renang pribadi dan sepenuhnya furnished.',
      es: 'En el corazón de un Bali auténtico y preservado, Terra Sky Kaba-Kaba ofrece una nueva visión del vivir tropical. Arquitectura contemporánea, naturaleza e intimidad se encuentran en esta residencia privada de 2.500 m². Desde lofts de 1 dormitorio hasta villas de 3 dormitorios, cada unidad cuenta con piscina privada y está completamente amueblada.'
    },
    location: 'Kaba-Kaba, Kediri, Bali',
    distances: {
      fr: ['Plage de Seseh — 3 min', 'Canggu — 10 min', 'Seminyak — 25 min', 'Aéroport — 35 min'],
      en: ['Seseh Beach — 3 min', 'Canggu — 10 min', 'Seminyak — 25 min', 'Airport — 35 min'],
      id: ['Pantai Seseh — 3 menit', 'Canggu — 10 menit', 'Seminyak — 25 menit', 'Bandara — 35 menit'],
      es: ['Playa Seseh — 3 min', 'Canggu — 10 min', 'Seminyak — 25 min', 'Aeropuerto — 35 min']
    },
    completion: {
      fr: 'Mars 2028',
      en: 'March 2028',
      id: 'Maret 2028',
      es: 'Marzo 2028'
    },
    leasehold: '30 + 20 ans',
    projectSize: '2 500 m²',
    // Real images extracted from the PDF (clean, without watermarks)
    images: [
      '/images/terra-sky/clean_15_0.jpeg',
      '/images/terra-sky/clean_18_1.jpeg',
      '/images/terra-sky/clean_20_1.jpeg',
      '/images/terra-sky/clean_22_2.jpeg',
      '/images/terra-sky/clean_28_0.jpeg',
      '/images/terra-sky/clean_30_1.jpeg',
      '/images/terra-sky/clean_35_2.jpeg'
    ],
    amenities: [
      { icon: 'pool', label: { fr: 'Piscine résidence', en: 'Residence Pool', id: 'Kolam Residensi', es: 'Piscina residencia' } },
      { icon: 'bar', label: { fr: 'Bar & Lounge', en: 'Bar & Lounge', id: 'Bar & Lounge', es: 'Bar & Lounge' } },
      { icon: 'cinema', label: { fr: 'Cinéma plein air', en: 'Outdoor Cinema', id: 'Bioskop Terbuka', es: 'Cine al aire libre' } },
      { icon: 'gym', label: { fr: 'Salle de fitness', en: 'Fitness Room', id: 'Ruang Fitness', es: 'Gimnasio' } },
      { icon: 'sauna', label: { fr: 'Sauna', en: 'Sauna', id: 'Sauna', es: 'Sauna' } },
      { icon: 'reception', label: { fr: 'Réception', en: 'Reception', id: 'Resepsi', es: 'Recepción' } }
    ],
    units: [
      {
        type: 'loft',
        name: { fr: 'Loft 1 Chambre', en: '1-Bedroom Loft', id: 'Loft 1 Kamar', es: 'Loft 1 Habitación' },
        priceEur: 116900,
        priceEurRooftop: 126900,
        landSize: 60,
        bedrooms: 1,
        bathrooms: 1,
        poolSize: '2 x 3 m',
        avgNightlyRate: 120,
        pdfUrl: '/documents/Bali_Life_x_Terra_Sky_Loft_1_Chambre.pdf',
        features: {
          fr: ['Piscine privée 6m²', 'Salon intégré signature', 'Suite avec dressing', 'Cuisine équipée', '2 niveaux'],
          en: ['Private pool 6m²', 'Signature sunken living', 'Suite with dressing', 'Equipped kitchen', '2 levels'],
          id: ['Kolam pribadi 6m²', 'Ruang tamu terintegrasi', 'Suite dengan dressing', 'Dapur lengkap', '2 lantai'],
          es: ['Piscina privada 6m²', 'Salón integrado exclusivo', 'Suite con vestidor', 'Cocina equipada', '2 niveles']
        }
      },
      {
        type: 'villa2',
        name: { fr: 'Villa 2 Chambres', en: '2-Bedroom Villa', id: 'Villa 2 Kamar', es: 'Villa 2 Habitaciones' },
        priceEur: 185900,
        priceEurRooftop: 199900,
        landSize: 110,
        bedrooms: 2,
        bathrooms: 2,
        poolSize: '3 x 6 m',
        avgNightlyRate: 180,
        pdfUrl: '/documents/Bali_Life_x_Terra_Sky_Villa_2_Chambres.pdf',
        features: {
          fr: ['Piscine privée 18m²', '2 suites indépendantes', 'Chaque chambre avec SDB', 'Terrasse principale 14m²', 'Option rooftop'],
          en: ['Private pool 18m²', '2 independent suites', 'Each room with bathroom', 'Main terrace 14m²', 'Rooftop option'],
          id: ['Kolam pribadi 18m²', '2 suite independen', 'Tiap kamar dengan kamar mandi', 'Teras utama 14m²', 'Opsi rooftop'],
          es: ['Piscina privada 18m²', '2 suites independientes', 'Cada habitación con baño', 'Terraza principal 14m²', 'Opción rooftop']
        }
      },
      {
        type: 'villa3',
        name: { fr: 'Villa 3 Chambres', en: '3-Bedroom Villa', id: 'Villa 3 Kamar', es: 'Villa 3 Habitaciones' },
        priceEur: 236900,
        priceEurRooftop: 256900,
        landSize: 160,
        bedrooms: 3,
        bathrooms: 3,
        poolSize: '3 x 7 m',
        avgNightlyRate: 250,
        pdfUrl: '/documents/Bali_Life_x_Terra_Sky_Villa_3_Chambres.pdf',
        features: {
          fr: ['Piscine privée 21m²', '3 suites avec SDB privative', 'Master suite premium', 'Terrasse 25m²', 'Chambre RDC indépendante'],
          en: ['Private pool 21m²', '3 suites with private bath', 'Premium master suite', 'Terrace 25m²', 'Independent ground floor room'],
          id: ['Kolam pribadi 21m²', '3 suite dengan kamar mandi pribadi', 'Master suite premium', 'Teras 25m²', 'Kamar lantai dasar independen'],
          es: ['Piscina privada 21m²', '3 suites con baño privado', 'Master suite premium', 'Terraza 25m²', 'Habitación planta baja independiente']
        }
      }
    ],
    paymentPlan: {
      fr: ['10% à la réservation', '35% à la signature notariale', '3 x 15% selon avancement', '10% à la remise des clés'],
      en: ['10% at booking', '35% at notarial signature', '3 x 15% per progress', '10% at key handover'],
      id: ['10% saat pemesanan', '35% saat penandatanganan notaris', '3 x 15% per progres', '10% saat serah terima kunci'],
      es: ['10% en la reserva', '35% en firma notarial', '3 x 15% según avance', '10% en entrega de llaves']
    }
  };

  const currentUnit = project.units[activeUnit];

  // Calculate ROI
  const calculateROI = () => {
    const priceUSD = currentUnit.priceEur * 1.08; // EUR to USD conversion
    const annualNights = 365 * (roiInputs.occupancyRate / 100);
    const annualRevenue = annualNights * roiInputs.nightlyRate;
    const netIncome = annualRevenue - roiInputs.annualExpenses;
    const roiPercentage = (netIncome / priceUSD) * 100;
    const totalReturn = netIncome * roiInputs.investmentYears;
    const paybackYears = priceUSD / netIncome;
    
    return {
      annualRevenue: Math.round(annualRevenue),
      netIncome: Math.round(netIncome),
      roiPercentage: roiPercentage.toFixed(1),
      totalReturn: Math.round(totalReturn),
      paybackYears: paybackYears.toFixed(1)
    };
  };

  const roiResults = calculateROI();

  // Auto-play gallery
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => 
        prev === project.images.length - 1 ? 0 : prev + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, project.images.length]);

  // Update ROI inputs when unit changes
  useEffect(() => {
    setRoiInputs(prev => ({
      ...prev,
      nightlyRate: currentUnit.avgNightlyRate
    }));
  }, [activeUnit, currentUnit.avgNightlyRate]);

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prev) => 
      prev === project.images.length - 1 ? 0 : prev + 1
    );
  }, [project.images.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? project.images.length - 1 : prev - 1
    );
  }, [project.images.length]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatUSD = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const AmenityIcon = ({ icon }) => {
    switch (icon) {
      case 'pool': return <Waves className="w-5 h-5" />;
      case 'bar': return <Sparkles className="w-5 h-5" />;
      case 'cinema': return <Film className="w-5 h-5" />;
      case 'gym': return <Dumbbell className="w-5 h-5" />;
      case 'sauna': return <Sparkles className="w-5 h-5" />;
      case 'reception': return <Users className="w-5 h-5" />;
      default: return null;
    }
  };

  const translations = {
    overline: { fr: 'Projet Exclusif', en: 'Exclusive Project', id: 'Proyek Eksklusif', es: 'Proyecto Exclusivo' },
    title: { fr: 'Projet Immobilier', en: 'Real Estate Project', id: 'Proyek Properti', es: 'Proyecto Inmobiliario' },
    subtitle: { 
      fr: 'Découvrez notre projet immobilier d\'exception en partenariat exclusif.', 
      en: 'Discover our exceptional real estate project in exclusive partnership.',
      id: 'Temukan proyek properti eksklusif kami dalam kemitraan eksklusif.',
      es: 'Descubra nuestro excepcional proyecto inmobiliario en asociación exclusiva.'
    },
    startingFrom: { fr: 'À partir de', en: 'Starting from', id: 'Mulai dari', es: 'Desde' },
    withRooftop: { fr: 'avec Rooftop', en: 'with Rooftop', id: 'dengan Rooftop', es: 'con Rooftop' },
    completion: { fr: 'Livraison prévue', en: 'Expected delivery', id: 'Perkiraan selesai', es: 'Entrega prevista' },
    leasehold: { fr: 'Durée du bail', en: 'Lease duration', id: 'Durasi sewa', es: 'Duración del arrendamiento' },
    pool: { fr: 'Piscine', en: 'Pool', id: 'Kolam', es: 'Piscina' },
    viewGallery: { fr: 'Voir la galerie', en: 'View gallery', id: 'Lihat galeri', es: 'Ver galería' },
    downloadBrochure: { fr: 'Télécharger la brochure', en: 'Download brochure', id: 'Unduh brosur', es: 'Descargar folleto' },
    requestAppointment: { fr: 'Demander un RDV', en: 'Request appointment', id: 'Minta janji temu', es: 'Solicitar cita' },
    keyFeatures: { fr: 'Points forts', en: 'Key Features', id: 'Fitur Utama', es: 'Características' },
    amenities: { fr: 'Espaces communs', en: 'Shared Amenities', id: 'Fasilitas Bersama', es: 'Amenidades' },
    chooseUnit: { fr: 'Choisissez votre unité', en: 'Choose your unit', id: 'Pilih unit Anda', es: 'Elija su unidad' },
    paymentPlan: { fr: 'Plan de paiement', en: 'Payment Plan', id: 'Rencana Pembayaran', es: 'Plan de Pago' },
    secureInvestment: { fr: 'Projet sécurisé', en: 'Secure project', id: 'Proyek aman', es: 'Proyecto seguro' },
    location: { fr: 'Localisation', en: 'Location', id: 'Lokasi', es: 'Ubicación' },
    roiCalculator: { fr: 'Simulateur ROI', en: 'ROI Calculator', id: 'Kalkulator ROI', es: 'Calculadora ROI' },
    occupancyRate: { fr: 'Taux d\'occupation', en: 'Occupancy Rate', id: 'Tingkat hunian', es: 'Tasa de ocupación' },
    nightlyRate: { fr: 'Tarif/nuit (USD)', en: 'Nightly Rate (USD)', id: 'Tarif/malam (USD)', es: 'Tarifa/noche (USD)' },
    annualExpenses: { fr: 'Charges annuelles', en: 'Annual Expenses', id: 'Biaya tahunan', es: 'Gastos anuales' },
    investmentPeriod: { fr: 'Durée du projet', en: 'Project Period', id: 'Periode proyek', es: 'Período del proyecto' },
    annualRevenue: { fr: 'Revenus annuels', en: 'Annual Revenue', id: 'Pendapatan tahunan', es: 'Ingresos anuales' },
    netIncome: { fr: 'Revenu net annuel', en: 'Net Annual Income', id: 'Pendapatan bersih', es: 'Ingreso neto anual' },
    roiPercentage: { fr: 'ROI annuel', en: 'Annual ROI', id: 'ROI tahunan', es: 'ROI anual' },
    totalReturn: { fr: 'Retour total', en: 'Total Return', id: 'Total pengembalian', es: 'Retorno total' },
    paybackPeriod: { fr: 'Retour sur invest.', en: 'Payback Period', id: 'Periode pengembalian', es: 'Período de recuperación' },
    years: { fr: 'ans', en: 'years', id: 'tahun', es: 'años' },
    calculateROI: { fr: 'Calculer le rendement', en: 'Calculate yield', id: 'Hitung yield', es: 'Calcular rendimiento' },
    roiDisclaimer: { 
      fr: '* Estimation brute, hors frais de gestion & taxes', 
      en: '* Gross estimate, excluding management fees & taxes', 
      id: '* Estimasi kasar, tidak termasuk biaya pengelolaan & pajak', 
      es: '* Estimación bruta, sin gastos de gestión ni impuestos' 
    },
    masterplan: { fr: 'Plan Architectural', en: 'Masterplan', id: 'Masterplan', es: 'Plan Maestro' },
    viewMasterplan: { fr: 'Voir le plan', en: 'View plan', id: 'Lihat rencana', es: 'Ver plano' },
    downloadMasterplan: { fr: 'Télécharger le plan PDF', en: 'Download PDF plan', id: 'Unduh rencana PDF', es: 'Descargar plano PDF' }
  };

  const t = (key) => translations[key]?.[language] || translations[key]?.['en'] || key;

  return (
    <section id="properties" className="py-20 lg:py-28 bg-[#F5F3EF]" data-testid="project-showcase-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <p className="overline mb-4" data-testid="showcase-overline">{t('overline')}</p>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#1A1A2E] font-semibold mb-4">
            {t('title')}
          </h2>
          <p className="text-base lg:text-lg text-[#4A4A5A] max-w-2xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          
          {/* Left: Image Gallery */}
          <div className="relative" data-testid="project-gallery">
            {/* Main Image */}
            <div 
              className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl cursor-pointer group"
              onClick={() => setShowGalleryModal(true)}
            >
              <img
                src={project.images[currentImageIndex]}
                alt={project.name[language]}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80';
                }}
              />
              
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span className="text-white font-medium bg-black/50 px-4 py-2 rounded-full text-sm">
                  {t('viewGallery')}
                </span>
              </div>

              {/* Navigation Arrows - Always visible on mobile, hover on desktop */}
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                data-testid="gallery-prev"
              >
                <ChevronLeft className="w-5 h-5 text-[#1A1A2E]" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                data-testid="gallery-next"
              >
                <ChevronRight className="w-5 h-5 text-[#1A1A2E]" />
              </button>

              {/* Play/Pause Button */}
              <button
                onClick={(e) => { e.stopPropagation(); setIsAutoPlaying(!isAutoPlaying); }}
                className="absolute bottom-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-all"
                data-testid="gallery-autoplay"
              >
                {isAutoPlaying ? <Pause className="w-4 h-4 text-[#1A1A2E]" /> : <Play className="w-4 h-4 text-[#1A1A2E]" />}
              </button>

              {/* Image Counter */}
              <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-white/90 rounded-full text-sm font-medium text-[#1A1A2E]">
                {currentImageIndex + 1} / {project.images.length}
              </div>
            </div>

            {/* Thumbnail Strip */}
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-hide">
              {project.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden transition-all ${
                    currentImageIndex === index 
                      ? 'ring-2 ring-[#C9A227] ring-offset-2' 
                      : 'opacity-60 hover:opacity-100'
                  }`}
                  data-testid={`thumbnail-${index}`}
                >
                  <img 
                    src={image} 
                    alt="" 
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200&q=60';
                    }}
                  />
                </button>
              ))}
            </div>

            {/* Amenities Grid */}
            <div className="mt-6 p-5 bg-white rounded-2xl shadow-sm">
              <h4 className="font-semibold text-[#1A1A2E] mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#C9A227]" />
                {t('amenities')}
              </h4>
              <div className="grid grid-cols-3 gap-3">
                {project.amenities.map((amenity, index) => (
                  <div key={index} className="flex flex-col items-center gap-2 p-3 bg-[#F5F3EF] rounded-xl text-center">
                    <div className="w-10 h-10 rounded-full bg-[#C9A227]/10 flex items-center justify-center text-[#C9A227]">
                      <AmenityIcon icon={amenity.icon} />
                    </div>
                    <span className="text-xs text-[#4A4A5A]">{amenity.label[language]}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Masterplan Section */}
            <div className="mt-6 p-5 bg-gradient-to-br from-[#1A1A2E] to-[#2A2A3E] rounded-2xl shadow-lg">
              <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#C9A227]" />
                {t('masterplan')}
              </h4>
              <div 
                className="relative rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => setShowMasterplan(true)}
              >
                <img 
                  src="/images/terra-sky/masterplan_preview.png" 
                  alt="Masterplan Terra Sky Kaba-Kaba"
                  className="w-full h-auto rounded-xl transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white font-medium bg-[#C9A227] px-4 py-2 rounded-full text-sm">
                    {t('viewMasterplan')}
                  </span>
                </div>
              </div>
              <a 
                href="/documents/masterplan.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="mt-4 w-full py-3 bg-[#C9A227] text-white rounded-xl flex items-center justify-center gap-2 hover:bg-[#B8922B] transition-colors text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                {t('downloadMasterplan')}
              </a>
            </div>
          </div>

          {/* Right: Project Details */}
          <div className="space-y-6" data-testid="project-details">
            {/* Project Header */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-[#C9A227]" />
                <span className="text-[#4A4A5A] text-sm">{project.location}</span>
              </div>
              <h3 className="font-playfair text-2xl lg:text-3xl text-[#1A1A2E] font-semibold mb-1">
                {project.name[language]}
              </h3>
              <p className="text-lg text-[#1A1A2E] font-medium mb-2">
                {project.subtitle[language]}
              </p>
              <p className="text-[#C9A227] font-medium italic">
                {project.tagline[language]}
              </p>
            </div>

            {/* Unit Type Selector */}
            <div>
              <p className="text-sm text-[#8A8A9A] mb-3">{t('chooseUnit')}</p>
              <div className="flex gap-2">
                {project.units.map((unit, index) => (
                  <button
                    key={unit.type}
                    onClick={() => setActiveUnit(index)}
                    className={`flex-1 py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                      activeUnit === index
                        ? 'bg-[#C9A227] text-white shadow-sm'
                        : 'bg-white text-[#4A4A5A] hover:bg-[#F5F3EF] border border-[#E5E5E5]'
                    }`}
                    data-testid={`unit-tab-${index}`}
                  >
                    <span className="block font-semibold">{unit.bedrooms} CH</span>
                    <span className="block text-xs opacity-80">{unit.landSize} m²</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Price Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#C9A227]/10">
              <p className="text-[#8A8A9A] text-sm mb-1">{t('startingFrom')}</p>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-playfair text-3xl lg:text-4xl text-[#1A1A2E] font-semibold">
                  {formatPrice(currentUnit.priceEur)}
                </span>
              </div>
              <p className="text-sm text-[#C9A227]">
                {formatPrice(currentUnit.priceEurRooftop)} {t('withRooftop')}
              </p>
              
              {/* Unit Stats */}
              <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-[#F5F3EF]">
                <div className="text-center">
                  <div className="flex items-center justify-center text-[#C9A227] mb-1">
                    <Bed className="w-5 h-5" />
                  </div>
                  <p className="text-[#1A1A2E] font-bold">{currentUnit.bedrooms}</p>
                  <p className="text-[#8A8A9A] text-xs">{language === 'fr' ? 'Chambre(s)' : 'Bedroom(s)'}</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center text-[#C9A227] mb-1">
                    <Bath className="w-5 h-5" />
                  </div>
                  <p className="text-[#1A1A2E] font-bold">{currentUnit.bathrooms}</p>
                  <p className="text-[#8A8A9A] text-xs">{language === 'fr' ? 'SDB' : 'Bath'}</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center text-[#C9A227] mb-1">
                    <Maximize className="w-5 h-5" />
                  </div>
                  <p className="text-[#1A1A2E] font-bold">{currentUnit.landSize}</p>
                  <p className="text-[#8A8A9A] text-xs">m²</p>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center text-[#C9A227] mb-1">
                    <Waves className="w-5 h-5" />
                  </div>
                  <p className="text-[#1A1A2E] font-bold text-sm">{currentUnit.poolSize}</p>
                  <p className="text-[#8A8A9A] text-xs">{t('pool')}</p>
                </div>
              </div>
            </div>

            {/* ROI Calculator Button */}
            <button
              onClick={() => {
                setShowROICalculator(!showROICalculator);
                // Track ROI calculator usage in Google Analytics
                if (!showROICalculator && window.trackROICalculator) {
                  window.trackROICalculator(currentUnit.name[language]);
                }
              }}
              className="w-full py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl flex items-center justify-center gap-3 hover:from-emerald-500 hover:to-emerald-600 transition-all shadow-sm"
              data-testid="roi-calculator-toggle"
            >
              <Calculator className="w-5 h-5" />
              <span className="font-medium">{t('roiCalculator')}</span>
              <TrendingUp className="w-5 h-5" />
            </button>

            {/* ROI Calculator Panel */}
            {showROICalculator && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-emerald-100 animate-fade-in" data-testid="roi-calculator-panel">
                <h4 className="font-semibold text-[#1A1A2E] mb-4 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-emerald-600" />
                  {t('roiCalculator')} - {currentUnit.name[language]}
                </h4>
                
                {/* Input Fields */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-xs text-[#8A8A9A] block mb-1">{t('occupancyRate')}</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={roiInputs.occupancyRate}
                        onChange={(e) => setRoiInputs({...roiInputs, occupancyRate: Number(e.target.value)})}
                        className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                        min="0"
                        max="100"
                      />
                      <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A9A]" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#8A8A9A] block mb-1">{t('nightlyRate')}</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={roiInputs.nightlyRate}
                        onChange={(e) => setRoiInputs({...roiInputs, nightlyRate: Number(e.target.value)})}
                        className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                        min="0"
                      />
                      <DollarSign className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A9A]" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#8A8A9A] block mb-1">{t('annualExpenses')} (USD)</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={roiInputs.annualExpenses}
                        onChange={(e) => setRoiInputs({...roiInputs, annualExpenses: Number(e.target.value)})}
                        className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                        min="0"
                      />
                      <DollarSign className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A9A]" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#8A8A9A] block mb-1">{t('investmentPeriod')}</label>
                    <div className="relative">
                      <input
                        type="number"
                        value={roiInputs.investmentYears}
                        onChange={(e) => setRoiInputs({...roiInputs, investmentYears: Number(e.target.value)})}
                        className="w-full px-3 py-2 border border-[#E5E5E5] rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                        min="1"
                        max="30"
                      />
                      <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8A9A]" />
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="bg-emerald-50 rounded-xl p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-xs text-[#8A8A9A]">{t('annualRevenue')}</p>
                      <p className="text-lg font-bold text-[#1A1A2E]">{formatUSD(roiResults.annualRevenue)}</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-xs text-[#8A8A9A]">{t('netIncome')}</p>
                      <p className="text-lg font-bold text-emerald-600">{formatUSD(roiResults.netIncome)}</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-xs text-[#8A8A9A]">{t('roiPercentage')}</p>
                      <p className="text-lg font-bold text-[#C9A227]">{roiResults.roiPercentage}%</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-xs text-[#8A8A9A]">{t('paybackPeriod')}</p>
                      <p className="text-lg font-bold text-[#1A1A2E]">{roiResults.paybackYears} {t('years')}</p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-emerald-600 rounded-lg text-center">
                    <p className="text-xs text-emerald-100">{t('totalReturn')} ({roiInputs.investmentYears} {t('years')})</p>
                    <p className="text-2xl font-bold text-white">{formatUSD(roiResults.totalReturn)}</p>
                  </div>
                  {/* ROI Disclaimer */}
                  <p className="mt-3 text-xs text-center text-[#8A8A9A] italic">
                    {t('roiDisclaimer')}
                  </p>
                </div>
              </div>
            )}

            {/* Unit Features */}
            <div className="bg-white rounded-2xl p-5 shadow-sm">
              <h4 className="font-semibold text-[#1A1A2E] mb-3 text-sm flex items-center gap-2">
                <Award className="w-4 h-4 text-[#C9A227]" />
                {currentUnit.name[language]} - {t('keyFeatures')}
              </h4>
              <div className="grid grid-cols-1 gap-2">
                {currentUnit.features[language].map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm text-[#4A4A5A]">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#C9A227]" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Key Info Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm">
                <Calendar className="w-5 h-5 text-[#C9A227]" />
                <div>
                  <p className="text-xs text-[#8A8A9A]">{t('completion')}</p>
                  <p className="font-medium text-[#1A1A2E] text-sm">{project.completion[language]}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-sm">
                <Shield className="w-5 h-5 text-[#C9A227]" />
                <div>
                  <p className="text-xs text-[#8A8A9A]">{t('leasehold')}</p>
                  <p className="font-medium text-[#1A1A2E] text-sm">{project.leasehold}</p>
                </div>
              </div>
            </div>

            {/* Distances */}
            <div className="p-4 bg-[#1A1A2E] rounded-xl text-white">
              <h4 className="font-semibold mb-3 text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#C9A227]" />
                {t('location')}
              </h4>
              <div className="grid grid-cols-2 gap-2">
                {project.distances[language].map((distance, index) => (
                  <p key={index} className="text-white/80 text-xs">{distance}</p>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <a
                href={currentUnit.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 btn-gold flex items-center justify-center space-x-2"
                data-testid="download-brochure-btn"
                onClick={() => {
                  // Track brochure download in Google Analytics
                  if (window.trackBrochureDownload) {
                    window.trackBrochureDownload(`Bali Life x Terra Sky - ${currentUnit.name[language]}`);
                  }
                }}
              >
                <Download className="w-5 h-5" />
                <span>{t('downloadBrochure')} - {currentUnit.name[language]}</span>
              </a>
              
              <button
                onClick={() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                  // Track appointment request in Google Analytics
                  if (window.trackAppointmentRequest) {
                    window.trackAppointmentRequest(currentUnit.name[language]);
                  }
                }}
                className="flex-1 btn-outline-dark flex items-center justify-center space-x-2"
                data-testid="contact-project-btn"
              >
                <span>{t('requestAppointment')}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Trust Badge */}
            <div className="flex items-center justify-center gap-2 pt-2 text-xs text-[#8A8A9A]">
              <Shield className="w-4 h-4" />
              <span>{t('secureInvestment')}</span>
              <span>•</span>
              <span>Bali Life Concierge x Terra Sky</span>
            </div>
          </div>
        </div>

        {/* Payment Plan Section */}
        <div className="mt-16 p-8 bg-white rounded-2xl shadow-sm">
          <h3 className="font-playfair text-2xl text-[#1A1A2E] font-semibold text-center mb-8">
            {t('paymentPlan')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {project.paymentPlan[language].map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#C9A227] text-white font-bold flex items-center justify-center mx-auto mb-3">
                  {index + 1}
                </div>
                <p className="text-sm text-[#4A4A5A]">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery Modal */}
      {showGalleryModal && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" data-testid="gallery-modal">
          <button
            onClick={() => setShowGalleryModal(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <button
            onClick={prevImage}
            className="absolute left-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <img
            src={project.images[currentImageIndex]}
            alt=""
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80';
            }}
          />
          
          <button
            onClick={nextImage}
            className="absolute right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {project.images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentImageIndex === index ? 'bg-white w-6' : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Masterplan Modal */}
      {showMasterplan && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" data-testid="masterplan-modal">
          <button
            onClick={() => setShowMasterplan(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="max-w-4xl w-full">
            <img
              src="/images/terra-sky/masterplan_preview.png"
              alt="Masterplan Bali Life x Terra Sky Kaba-Kaba"
              className="w-full h-auto rounded-lg"
            />
            <div className="mt-4 flex justify-center">
              <a 
                href="/documents/masterplan.pdf" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-6 py-3 bg-[#C9A227] text-white rounded-full flex items-center gap-2 hover:bg-[#B8922B] transition-colors font-medium"
              >
                <Download className="w-5 h-5" />
                {t('downloadMasterplan')}
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProjectShowcase;
